import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, HelpCircle, Shield, Zap, HeartHandshake, Phone } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import ContactForm from "@/components/ContactForm";
import PaymentMethods from "@/components/PaymentMethods";
import ResponsiveAccordion from "@/components/ResponsiveAccordion";
import SocialProofStrip from "@/components/SocialProofStrip";
import WhatsAppButton from "@/components/WhatsAppButton";

import { pulseScale, pulseTransition } from "@/utils/motionVariants";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };
const viewportOnce = { once: true, amount: 0 };

const PLANS = [
  {
    name: "In-Person",
    desc: "Card machines for retail, hospitality and services",
    from: "0.5%",
    subrate: "Blended rate across all card types",
    unit: "typical debit rate",
    features: [
      "Debit cards from 0.5%, credit from 0.99%",
      "Contactless, chip & PIN, Apple Pay, Google Pay",
      "Next-day settlement into your UK bank account",
      "No minimum contract — leave anytime",
      "Free terminal swap if you're switching",
      "UK-based support in under 4 minutes",
    ],
    cta: "Get my in-person quote",
    link: "/contact?product=In-Person%20Payments&category=card-machines&source=pricing-page",
    highlight: false,
  },
  {
    name: "Online",
    desc: "Payment gateway for e-commerce and online stores",
    from: "1%",
    subrate: "+ 15p per transaction",
    unit: "beats Stripe on price and speed",
    features: [
      "Lower than Stripe (1.4% + 20p) on every transaction",
      "Money in your bank the next working day — not 5–7 days",
      "Visa, Mastercard, Amex, Apple Pay, Google Pay",
      "WooCommerce, Shopify, Magento 2 and more",
      "3D Secure and fraud protection built in",
      "Hosted or API integration options",
    ],
    cta: "Get my online quote",
    link: "/contact?product=Online%20Payments&category=payment-gateway&source=pricing-page",
    highlight: true,
  },
  {
    name: "EPOS",
    desc: "All-in-one point of sale for managing your business",
    from: "Custom",
    subrate: "Payments + EPOS bundled",
    unit: "based on your setup",
    features: [
      "Sales, stock, staff and reporting in one system",
      "Integrated card payments at in-person rates",
      "Table management for hospitality",
      "Cloud-based — access anywhere",
      "Hardware and software bundles",
      "Full training and onboarding included",
    ],
    cta: "Get my EPOS quote",
    link: "/contact?product=EPOS%20System&category=epos-systems&source=pricing-page",
    highlight: false,
  },
];

const FAQS = [
  { q: "Are these the exact rates I'll pay?", a: "These are our standard published rates. Your exact pricing may vary depending on your monthly turnover, average transaction value, and card mix. Higher-volume businesses may qualify for lower rates. We'll give you a precise, no-obligation quote based on your actual numbers." },
  { q: "Are there any hidden fees?", a: "No. We'll show you every line item before you sign anything. Your monthly statement will match your quote — guaranteed. If it doesn't, we'll fix it." },
  { q: "What about terminal rental costs?", a: "Terminal rental starts from £10/month depending on the device. We also offer purchase options. If you're switching from another provider, we may be able to swap your terminal for free." },
  { q: "How does your pricing compare to Stripe?", a: "Stripe charges 1.4% + 20p per transaction for UK cards, and settlement takes 5–7 working days. With CanDo, online rates start at 1% + 15p — that's a saving on every single transaction. Plus your money lands in your bank the next working day, not a week later. For a business processing £20,000/month online, that's roughly £100/month less in fees and significantly better cash flow." },
  { q: "How does your pricing compare to Square or SumUp?", a: "Square charges a flat 1.75% on all in-person transactions. SumUp charges 1.69%. Our in-person rates start from 0.5% for debit cards. For most businesses processing over £5,000/month, CanDo is significantly cheaper. Use our savings calculator to see the exact difference." },
  { q: "Is there a minimum contract?", a: "No. We don't lock you in. If you're not happy, you can leave. We keep our clients by being good at what we do — not by trapping them in contracts." },
  { q: "How quickly can I get set up?", a: "Order before 2pm and your terminal can be live the next working day. Online payment gateways can typically go live within 24-48 hours." },
  { q: "When do I get my money?", a: "Next working day. Unlike Stripe (5–7 days) or PayPal (3–5 days), your card sales are settled into your UK bank account the very next working day. Better cash flow from day one." },
];

// Hoisted motion configs — re-using stable references prevents framer-motion
// from resetting its interpolation state on every parent render.
const CARD_HOVER = { y: -8, transition: { type: "spring", stiffness: 300 } };
const CTA_TAP = { scale: 0.97 };

function PricingCard({ plan }) {
  return (
    <motion.div
      whileHover={CARD_HOVER}
      className={`rounded-2xl p-6 sm:p-8 flex flex-col h-full ${plan.highlight ? "bg-brand-black text-white ring-2 ring-brand-aqua shadow-xl relative" : "bg-white border border-brand-light-grey shadow-sm"}`}
      data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s/g, "-")}`}
    >
      {plan.highlight && <motion.div animate={pulseScale} transition={pulseTransition} className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-aqua text-white text-xs font-bold px-4 py-1 rounded-full">Most Popular</motion.div>}
      <div className="mb-6">
        <h3 className={`font-heading text-xl font-bold ${plan.highlight ? "text-white" : "text-brand-black"}`}>{plan.name}</h3>
        <p className={`text-sm mt-1 ${plan.highlight ? "text-white/60" : "text-brand-grey"}`}>{plan.desc}</p>
      </div>
      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className={`text-4xl font-heading font-bold ${plan.highlight ? "text-brand-aqua" : "text-brand-aqua-dark"}`}>{plan.from}</span>
          {plan.from !== "Custom" && plan.subrate && plan.subrate.startsWith("+") && <span className={`text-lg font-heading font-bold ${plan.highlight ? "text-brand-aqua/70" : "text-brand-aqua-dark/70"}`}>{plan.subrate}</span>}
        </div>
        {plan.subrate && !plan.subrate.startsWith("+") && <p className={`text-xs mt-1 font-semibold ${plan.highlight ? "text-brand-aqua/60" : "text-brand-grey"}`}>{plan.subrate}</p>}
        <p className={`text-xs mt-1 ${plan.highlight ? "text-white/40" : "text-brand-grey"}`}>{plan.unit}</p>
      </div>
      <div className="space-y-2.5 flex-1 mb-6">
        {plan.features.map((f) => (
          <div key={f} className={`flex items-start gap-2 text-sm ${plan.highlight ? "text-white/80" : "text-brand-grey"}`}>
            <CheckCircle size={14} className={`flex-shrink-0 mt-0.5 ${plan.highlight ? "text-brand-aqua" : "text-brand-aqua"}`} />{f}
          </div>
        ))}
      </div>
      <motion.div whileTap={CTA_TAP}>
        <Link to={plan.link} className={`block text-center font-semibold rounded-full px-6 py-3.5 text-sm transition-all ${plan.highlight ? "cta-primary text-white" : "bg-brand-aqua hover:bg-brand-aqua-dark text-white"}`}>
          {`${plan.cta} `}<ArrowRight size={14} className="inline ml-1" />
        </Link>
      </motion.div>
    </motion.div>
  );
}

function PricingFAQ() {
  return <ResponsiveAccordion items={FAQS} testIdPrefix="pricing-faq" />;
}

export default function PricingPage() {
  return (
    <div>
      <SEOHead title="Transparent Pricing Plans & Fees" description="Discover competitive and clear pricing plans from CanDo Payments. We offer flexible payment packages designed to suit businesses of all sizes across the UK." path="/pricing" faqs={FAQS} />

      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua mb-3">Pricing</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-4">Honest pricing. No surprises. <span className="gradient-text">Ever.</span></motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed max-w-2xl mx-auto mb-6">Clear rates, no hidden fees, and your money in the bank the next working day — not next week. Lower than Stripe for online. Lower than Square for in-person. Backed by Elavon.</motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4 text-sm text-brand-black">
              <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> No hidden fees</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> No minimum contract</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> Next-day settlement</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-brand-aqua" /> Switch for free</span>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-8 flex justify-center">
              <SocialProofStrip
                reassurance="Rated 4.92/5 by UK businesses"
                testId="pricing-social-proof"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="py-16 md:py-20 bg-gray-50" data-testid="pricing-cards">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {PLANS.map((p) => <PricingCard key={p.name} plan={p} />)}
          </div>
          <p className="text-xs text-brand-grey text-center mt-6">*Rates shown are standard published rates. Higher-volume businesses may qualify for preferential pricing. <Link to="/switch-and-save" className="text-brand-aqua hover:underline">Use our calculator</Link> for a personalised estimate.</p>
        </div>
      </section>

      {/* Worked example */}
      <section className="py-12 bg-white border-b border-brand-light-grey/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6 sm:p-8">
            <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua mb-3">Worked Example</p>
            <h3 className="font-heading text-lg font-bold text-brand-black mb-4">What would a typical business actually pay?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-white border border-brand-light-grey rounded-xl p-4">
                <p className="text-xs text-brand-grey uppercase tracking-wider mb-1">Your business</p>
                <p className="text-sm text-brand-black">Card turnover: <strong>£15,000/mo</strong></p>
                <p className="text-sm text-brand-black">Avg transaction: <strong>£25</strong></p>
                <p className="text-sm text-brand-black">600 transactions/month</p>
              </div>
              <div className="bg-white border border-brand-light-grey rounded-xl p-4">
                <p className="text-xs text-brand-grey uppercase tracking-wider mb-1">In-person with CanDo</p>
                <p className="text-2xl font-heading font-bold text-brand-aqua-dark">~£105–£125/mo</p>
                <p className="text-xs text-brand-grey mt-1">0.5% debit + 0.99% credit + fees</p>
                <p className="text-xs text-brand-pink font-semibold mt-2">Save £60–£150/mo vs major providers</p>
              </div>
              <div className="bg-white border border-brand-pink/20 rounded-xl p-4">
                <p className="text-xs text-brand-grey uppercase tracking-wider mb-1">Online with CanDo</p>
                <p className="text-2xl font-heading font-bold text-brand-pink">1% + 15p</p>
                <p className="text-xs text-brand-grey mt-1">vs Stripe 1.4% + 20p</p>
                <p className="text-xs text-brand-aqua font-semibold mt-2">+ next-day settlement (not 5–7 days)</p>
              </div>
            </div>
            <p className="text-xs text-brand-grey">Worked example based on typical in-person card mix (70% debit, 20% credit, 10% business). Online comparison based on UK domestic cards. Your actual costs depend on your specific profile. <Link to="/switch-and-save" className="text-brand-aqua hover:underline">Use our calculator</Link> for a personalised estimate.</p>
          </div>
        </div>
      </section>

      {/* Why our pricing is different */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-8 md:mb-12">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">The CanDo Difference</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">Why businesses pay less with us</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "No hidden charges", desc: "No PCI non-compliance fees, no statement fees, no minimum monthly charges. What we quote is what you pay." },
              { icon: Zap, title: "Rates matched to you", desc: "We don't apply blanket pricing. Your rate is based on your actual transaction volume and card mix." },
              { icon: HeartHandshake, title: "Free switching", desc: "We handle the migration, swap your terminal, and get you live — usually next working day. No setup fees." },
              { icon: HelpCircle, title: "We explain everything", desc: "Your statement will make sense. No jargon, no mystery charges. And if you don't understand something, call us." },
            ].map((item) => (
              <div key={item.title} className="bg-gray-50 border border-brand-light-grey rounded-2xl p-6">
                <item.icon size={24} className="text-brand-aqua mb-4" />
                <h3 className="font-heading font-bold text-brand-black mb-2">{item.title}</h3>
                <p className="text-sm text-brand-grey leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator CTA */}
      <section className="py-12 bg-brand-aqua-light border-y border-brand-aqua/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-brand-black mb-3">Want to see your exact savings?</h2>
          <p className="text-sm text-brand-grey mb-5">Enter your current charges and our calculator will show you a side-by-side comparison in 30 seconds.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/switch-and-save" className="inline-flex items-center gap-2 cta-primary text-white font-semibold rounded-full px-8 py-3.5 text-sm transition-all btn-shine">
              Open Savings Calculator <ArrowRight size={14} />
            </Link>
            <WhatsAppButton variant="secondary" placement="pricing" label="WhatsApp us your rate" />
          </div>
          <p className="text-xs text-brand-grey mt-3">Not sure if you&apos;re overpaying? WhatsApp us your current rate and we&apos;ll tell you straight.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white" data-testid="pricing-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Pricing FAQs</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight">Common questions about our pricing</h2>
          </div>
          <PricingFAQ />
        </div>
      </section>

      {/* Trust */}
      <section className="py-10 bg-gray-50 border-y border-brand-light-grey/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <PaymentMethods size="md" />
        </div>
      </section>

      {/* CTA with form */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Get Your Quote</p>
            <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-4">Ready to see what you&apos;d actually pay?</h2>
            <p className="text-brand-grey leading-relaxed mb-6">Tell us about your business and we&apos;ll send you a clear, line-by-line quote — usually within a few hours. No obligation. No hard sell.</p>
            <div className="space-y-3 mb-8">
              {["Personalised quote based on your actual numbers", "Side-by-side comparison with your current provider", "Clear breakdown of every charge", "Delivered within 24 hours"].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
                  <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 bg-gray-50 border border-brand-light-grey rounded-xl p-4">
              <Phone size={18} className="text-brand-aqua flex-shrink-0" />
              <div>
                <p className="text-xs text-brand-grey">Prefer to talk?</p>
                <a href="tel:+448003682989" className="text-sm font-bold text-brand-black hover:text-brand-aqua transition-colors">0800 368 2989</a>
              </div>
            </div>
          </div>
          <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
            <h3 className="font-heading text-xl font-bold text-brand-black mb-2">Get your personalised quote</h3>
            <p className="text-sm text-brand-grey mb-4">No obligation. No hard sell. Just clear numbers.</p>
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
